import styled from '@emotion/styled';
import { Box, Button, Stack } from '@mantine/core';
import { ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toastError, toastSuccess } from '../../config/Helper';
import { ROUTES } from '../../config/RoutesName';
import { paymentService } from '../../services/order/order.service';
import { AxiosResponse, isAxiosError } from 'axios';
import { IBaseResponseError } from '../../typescript/interfaces/common.interface';
import { AvailablePaymentMethods, StripeExpressCheckoutElementReadyEvent } from '@stripe/stripe-js';

const CustomPaymentElement = styled(PaymentElement)`
  iframe {
    max-height: 100svh;
    height: fit-content;
    margin-bottom: 20px !important;
  }
`;

const CustomExpressCheckoutElement = styled(ExpressCheckoutElement)`
  iframe {
    margin-bottom: '20px';
  }
`;

function CheckoutForm() {
  const [errorMessage, setErrorMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [load, setLoad] = useState(false);

  const submitPayment = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setLoad(true);
    if (elements == null) {
      return;
    }

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      // Show error to your customer
      setLoad(false);
      setErrorMessage(submitError.message ?? '');
      return;
    }

    if (stripe) {
      const { error } = await stripe.confirmPayment({
        //`Elements` instance that was used to create the Payment Element
        elements,
        clientSecret: location.state.clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: 'https://example.com/order/123/complete',
        },
      });

      if (error) {
        // This point will only be reached if there is an immediate error when
        // confirming the payment. Show error to your customer (for example, payment
        // details incomplete)
        setLoad(false);
        setErrorMessage(error.message ?? '');
      } else {
        // Your customer will be redirected to your `return_url`. For some payment
        // methods like iDEAL, your customer will be redirected to an intermediate
        // site first to authorize the payment, then redirected to the `return_url`.
      }
    }

    try {
      const data = await paymentService(location.state.orderId as string);
      if (data?.success) {
        toastSuccess(data?.message);
        setLoad(false);
        setErrorMessage('');
        navigate(ROUTES.ORDERDETAILS + '/' + data.data.order_id);
      } else {
        toastError(data?.message);
        setLoad(false);
        setErrorMessage('');
      }
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        const response = error.response as AxiosResponse<IBaseResponseError> | undefined;
        const data = response?.data;
        const hasApiError = data?.data?.error_type && data?.data?.error_message;
        // toastError(hasApiError ? data : data?.message);
      }
      setErrorMessage('');
      console.log(error);
    }
  };

  const handleReady = (e: StripeExpressCheckoutElementReadyEvent) => {
    const { applePay, googlePay } = e.availablePaymentMethods as AvailablePaymentMethods;
    console.log({ applePay, googlePay }, 'handleReady');
  };

  return (
    <Box component='form' onSubmit={submitPayment} px={20}>
      <CustomExpressCheckoutElement
        options={{
          buttonType: { applePay: 'buy', googlePay: 'buy' },
          buttonHeight: 48,
          buttonTheme: { applePay: 'black', googlePay: 'black' },
          paymentMethods: {
            applePay: 'always',
            googlePay: 'always',
          },
        }}
        onReady={handleReady}
        // onClick={()=>{}}
        onConfirm={() => submitPayment()}
      />
      <CustomPaymentElement
        onChange={({ complete }) => {
          if (complete) {
            setErrorMessage('');
          }
        }}
      />
      <Button loading={load} type='submit' disabled={!stripe || !elements} bg='magenta.9' fullWidth>
        Pay
      </Button>
      {/* Show error message to your customers */}
      {errorMessage && (
        <Box c='#df1b41' mt={10} fw={900}>
          {errorMessage}
        </Box>
      )}
    </Box>
  );
}

export default CheckoutForm;
